import React, { Component } from 'react';

import appSettings from '../../config/settings';
import * as log from '../../util/log';
import {
  isChunkLoadError,
  tryReloadOnceForChunkError,
} from '../../util/chunkLoadRecovery';

import { LoadableComponentErrorBoundaryPage } from './LoadableComponentErrorBoundaryPage';

// A non-chunk render error is deterministic (same props/data will crash the
// same way every time), so retrying it just remounts the subtree and
// crashes again -- without a cap this loops indefinitely (seen in
// production: a bad currency value crashed the dashboard 50+ times/sec
// until Render started 429ing the app's own API). Stop retrying once we've
// seen the same boundary fail this many times and show the fallback page
// instead of the children.
const MAX_NON_CHUNK_RETRIES = 3;

// Use ErrorBoyndary to catch ChunkLoadError
// https://reactjs.org/docs/error-boundaries.html
class LoadableComponentErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, retryCount: 0 };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI.
    return { error };
  }

  componentDidCatch(error) {
    if (isChunkLoadError(error)) {
      log.error(error, 'chunk-load-error', {
        pathname: typeof window !== 'undefined' ? window.location.pathname : null,
      });
      tryReloadOnceForChunkError();
      return;
    }

    log.error(error, 'loadable-component-error', {
      pathname: typeof window !== 'undefined' ? window.location.pathname : null,
      retryCount: this.state.retryCount + 1,
    });
    this.setState(state => ({ retryCount: state.retryCount + 1 }));
  }

  render() {
    if (this.state.error && isChunkLoadError(this.state.error)) {
      return <LoadableComponentErrorBoundaryPage />;
    }

    if (this.state.error && this.state.retryCount >= MAX_NON_CHUNK_RETRIES) {
      return <LoadableComponentErrorBoundaryPage />;
    }

    return this.props.children;
  }
}

// LoadableComponentErrorBoundary helps in situations
// where production build changes in the server and
// long-living client app tries to fetch code chunks that don't exist anymore.
// Note: in development mode with Hot Module Reloading (HMR) in use, this causes error loops.
const UseLoadableErrorBoundaryOnlyInProdutionMode = props => {
  const { children } = props;
  return appSettings.dev ? (
    children
  ) : (
    <LoadableComponentErrorBoundary>{children}</LoadableComponentErrorBoundary>
  );
};
export default UseLoadableErrorBoundaryOnlyInProdutionMode;
